import { gql, useMutation } from "@apollo/client";
import { Button, LinearProgress, Paper, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import BasicCard from "components/card/BasicCard";
import { ErrorBox } from "components/error";
import { useSolutionKey } from "features/solution";
import { Formik, FormikErrors, FormikValues } from "formik";
import { isEmpty } from "lodash";
import { ReleaseType } from "model/schema/globalTypes";
import { useEffect, useMemo } from "react";
import { Link, useHistory } from "react-router-dom";
import defaultIcon from "tile.svg";
import { isAsciiString } from "utils/addForm";
import { createRoute } from "utils/url";
import DataDependencies from "../components/DataDependencies";
import ChangesPreview from "./ChangesPreview";
import usePublishFormReducer, { State } from "./hooks/usePublishFormReducer";
import {
  useLazyQueryNewWebServicePreview,
  useLazyQueryUpgradeWebservicePreview
} from "./hooks/useQueryWebServicePreview";
import PublishForm from "./PublishForm";
import {
  PublishUseCase,
  PublishUseCaseVariables
} from "./schema/PublishUseCase";

const mutationPublishUseCase = gql`
  mutation PublishUseCase(
    $solutionKey: Key!
    $useCaseDefinition: UseCaseDefinitionInput!
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        useCases {
          addUseCase(useCaseDefinition: $useCaseDefinition) {
            detail {
              version
              key
            }
          }
        }
      }
    }
  }
`;

const useStyles = makeStyles((theme: Theme) => {
  const breakpointValue = 970;
  return createStyles({
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gridTemplateRows: "25px auto",
      padding: theme.spacing(3),
      columnGap: theme.spacing(3),
      rowGap: theme.spacing(3),
      width: "80%",
      [theme.breakpoints.down(breakpointValue)]: {
        gridTemplateColumns: "100%"
      }
    },
    row1: {
      gridRow: "1 / span 1",
      gridColumn: "1 / span 1",
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(1),
      [theme.breakpoints.down(breakpointValue)]: {
        gridColumn: "1 / -1"
      }
    },
    row2: {
      gridRow: "2 / span 1",
      gridColumn: "1 / span 1",
      [theme.breakpoints.down(breakpointValue)]: {
        gridColumn: "1 / -1"
      }
    },
    row3: {
      marginBottom: theme.spacing(1),
      color: theme.palette.primary.main
    },
    row4: {
      margin: theme.spacing(4, 0, 2, 0),

      width: 350,
      minHeight: 200,
      height: "auto"
    },
    row5: {
      width: "100%",
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2)
    },
    row6: {
      display: "inherit",
      width: "100%"
    },
    rightColumnWrapper: {
      gridRow: "1 / span 2",
      gridColumn: "2 / span 1",
      display: "flex",
      flexDirection: "column",
      [theme.breakpoints.down(breakpointValue)]: {
        gridRow: "auto",
        gridColumn: "1 / -1"
      }
    },
    errorBoxesContainer: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2)
    },
    flex: {
      display: "flex",
      flexDirection: "column"
    },
    indicatorWrapper: {
      position: "sticky",
      width: "100%",
      zIndex: 1300
    },
    link: { display: "inline-flex", textDecoration: "none" },
    cancelButton: {
      color: "#FFFFFF",
      marginRight: theme.spacing(1),
      "&:hover": {
        backgroundColor: theme.palette.primary.main,
        opacity: 0.8
      }
    },
    createButton: {
      color: "#FFFFFF",
      backgroundColor: theme.palette.primary.dark,
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
        opacity: 0.8
      }
    },
    disabledButton: {
      backgroundColor: "#C7CFD4 !important",
      color: "#68777B !important"
    },
    margin: {
      [theme.breakpoints.down(breakpointValue)]: { marginTop: theme.spacing(1) }
    },
    buttons: { marginTop: theme.spacing(5) }
  });
});

interface PublishFormWrapperProps {
  readonly initialState?: State;
  readonly updateUseCase?: boolean;
}

export default function PublishFormWrapper({
  initialState = {
    name: "",
    key: "",
    autoGeneratedKey: true,
    description: "",
    calculationRuleAddress: "",
    tags: [],
    transactionTable: null
  },
  updateUseCase = false
}: Readonly<PublishFormWrapperProps>) {
  const solutionKey = useSolutionKey();
  const classNames = useStyles({});
  const history = useHistory();
  const fixedUseCaseKey = initialState.key;

  const [
    publish,
    {
      loading: publishUseCaseLoading,
      error: publishUseCaseError,
      reset: resetPublishUseCaseError
    }
  ] = useMutation<PublishUseCase, PublishUseCaseVariables>(
    mutationPublishUseCase,
    {
      onCompleted: mutationResult => {
        const createdUseCase =
          mutationResult.solutions.solution?.useCases?.addUseCase?.detail;
        if (!createdUseCase) return;
        const { key, version } = createdUseCase;
        history.push(
          createRoute(
            `/solutions/${solutionKey}/create-use-case-version/${key}`
          )
        );
        history.push(
          createRoute(`/solutions/${solutionKey}/use-cases/${key}/${version}`)
        );
      }
    }
  );

  const [state, dispatch] = usePublishFormReducer(initialState);
  const [
    loadNewPreview,
    { data: newPreviewData, error: newPreviewError, loading: newPreviewLoading }
  ] = useLazyQueryNewWebServicePreview();
  const [
    loadUpgradePreview,
    {
      data: upgradePreviewData,
      error: upgradePreviewError,
      loading: upgradePreviewLoading
    }
  ] = useLazyQueryUpgradeWebservicePreview();

  const {
    name,
    image,
    description,
    tags,
    calculationRuleAddress,
    key,
    transactionTable
  } = state;
  const initialValues = {
    name,
    key,
    description,
    calculationRuleAddress: "",
    transactionTable
  };

  useEffect(() => {
    if (!calculationRuleAddress) {
      dispatch({ type: "LOAD_RULE", value: false });
      dispatch({ type: "LOAD_TRANSACTION_TABLE_NAMES", value: false });
      return;
    }

    if (updateUseCase) {
      loadUpgradePreview({
        variables: {
          solutionKey,
          useCaseKey: fixedUseCaseKey,
          calculationRuleAddress
        }
      });
      return;
    }

    loadNewPreview({
      variables: {
        solutionKey,
        calculationRuleAddress
      }
    });
  }, [
    calculationRuleAddress,
    dispatch,
    fixedUseCaseKey,
    loadNewPreview,
    loadUpgradePreview,
    solutionKey,
    updateUseCase
  ]);

  const [previewLoading, previewError] = updateUseCase
    ? [upgradePreviewLoading, upgradePreviewError]
    : [newPreviewLoading, newPreviewError];

  useEffect(() => {
    if (!calculationRuleAddress) {
      return;
    }

    dispatch({ type: "LOAD_RULE", value: previewLoading });
    dispatch({ type: "LOAD_TRANSACTION_TABLE_NAMES", value: previewLoading });
  }, [calculationRuleAddress, dispatch, previewLoading]);

  const allUseCasesKeys = useMemo(
    () =>
      calculationRuleAddress && !updateUseCase
        ? newPreviewData?.solutions.solution?.useCases?.useCases?.map(
            ({ detail: { key, version } }) => ({ key, version })
          )
        : undefined,
    [calculationRuleAddress, newPreviewData, updateUseCase]
  );

  const isExistedKey = (value: string) =>
    allUseCasesKeys?.find(useCase => useCase.key === value);

  const transactionTableNames = useMemo(() => {
    if (!calculationRuleAddress) {
      return [];
    }

    if (updateUseCase) {
      return (
        upgradePreviewData?.solutions.solution?.useCases?.useCase?.detail.nextVersion?.schema?.input.map(
          table => table.name
        ) ?? []
      );
    }

    return (
      newPreviewData?.solutions.solution?.useCases?.calculationRule?.schema?.input.map(
        table => table.name
      ) ?? []
    );
  }, [
    calculationRuleAddress,
    newPreviewData,
    updateUseCase,
    upgradePreviewData
  ]);

  useEffect(() => {
    dispatch({
      type: "SET_TRANSACTION_TABLE_NAMES",
      value: transactionTableNames
    });
  }, [dispatch, transactionTableNames]);

  const dataDependencies = useMemo(() => {
    if (!calculationRuleAddress) {
      return [];
    }

    if (updateUseCase) {
      return (
        upgradePreviewData?.solutions.solution?.useCases?.useCase?.detail
          .nextVersion?.dataDependencies ?? []
      );
    }

    return (
      newPreviewData?.solutions.solution?.useCases?.calculationRule
        ?.dataDependencies ?? []
    );
  }, [
    calculationRuleAddress,
    newPreviewData,
    updateUseCase,
    upgradePreviewData
  ]);

  const upgradeNextVersion = updateUseCase
    ? upgradePreviewData?.solutions.solution?.useCases?.useCase?.detail
        .nextVersion
    : undefined;
  const newWebServiceSchema = !updateUseCase
    ? newPreviewData?.solutions.solution?.useCases?.calculationRule?.schema
    : undefined;

  const loading = [publishUseCaseLoading, previewLoading].some(Boolean);

  const validate = (values: FormikValues) => {
    const errors: FormikErrors<FormikValues> = {};
    if (!updateUseCase) {
      if (values.key === "") {
        errors.key = "Web service Key is required";
      } else if (isExistedKey(values.key)) {
        errors.key = "This key already exists. You can publish a new version";
      } else if (/[^a-z0-9-]/.test(values.key)) {
        errors.key =
          "Key must only contain lowercase letters, digits and dashes";
      }
      if (
        values.name &&
        (values.key || key) &&
        !isExistedKey(values.key) &&
        !values.calculationRuleAddress
      ) {
        errors.calculationRuleAddress = "Calculation Rule is required";
      }
    } else if (values.name && !values.calculationRuleAddress) {
      errors.calculationRuleAddress = "Calculation Rule is required";
    }
    if (values.name === "") {
      errors.name = "Web service Name is required";
    } else if (!isAsciiString(values.name)) {
      errors.name = "Name must have only Ascii characters";
    }

    if (!Boolean(values.transactionTable)) {
      errors.transactionTable = "Transaction Table is required";
    }

    return errors;
  };

  return (
    <>
      <div className={classNames.indicatorWrapper}>
        {loading ? <LinearProgress /> : null}
      </div>
      <Paper className={classNames.grid}>
        <Typography className={classNames.row1}>
          {updateUseCase ? "NEW WEB SERVICE VERSION" : "NEW WEB SERVICE"}
        </Typography>
        <div className={classNames.row2}>
          <Formik
            initialValues={initialValues}
            validate={validate}
            onSubmit={(values, { setSubmitting }) => {
              setTimeout(() => {
                setSubmitting(false);
              }, 500);
            }}
          >
            {({ submitForm, dirty, isValid, ...restOfProps }) => {
              return (
                <div className={classNames.flex}>
                  <PublishForm
                    {...restOfProps}
                    solutionKey={solutionKey}
                    state={state}
                    dispatch={dispatch}
                    updateUseCase={updateUseCase}
                    version={isExistedKey(key)?.version}
                  />
                  <div className={classNames.buttons}>
                    <Link
                      className={classNames.link}
                      to={createRoute(`/solutions/${solutionKey}/use-cases`)}
                    >
                      <Button
                        color="primary"
                        variant="contained"
                        className={classNames.cancelButton}
                      >
                        Cancel
                      </Button>
                    </Link>
                    <Button
                      disabled={loading || !dirty || !isValid}
                      id="publish-use-case"
                      variant="contained"
                      classes={{
                        root: classNames.createButton,
                        disabled: classNames.disabledButton
                      }}
                      className={classNames.createButton}
                      data-testid={
                        !updateUseCase
                          ? "publish-use-case"
                          : "publish-use-case-version"
                      }
                      onClick={() => {
                        submitForm();
                        if (!state.calculationRuleAddress) return;
                        publish({
                          variables: {
                            solutionKey,
                            useCaseDefinition: {
                              calculationRuleAddress:
                                state.calculationRuleAddress,
                              key: state.key,
                              name: state.name,
                              description: state.description,
                              transactionTable: state.transactionTable,
                              image: state.image,
                              tags: state.tags
                            }
                          }
                        });
                      }}
                    >
                      {!updateUseCase
                        ? "Create Web Service"
                        : "Create new version"}
                    </Button>
                  </div>
                </div>
              );
            }}
          </Formik>
        </div>
        <div className={classNames.rightColumnWrapper}>
          <Typography align="left" className={classNames.row3}>
            PREVIEW
          </Typography>
          <div className={classNames.row4} data-testid="preview">
            <BasicCard
              name={name}
              key={!isEmpty(key) ? key : "new-use-case"}
              tags={tags}
              icon={image ? image : defaultIcon}
              description={description}
            />
          </div>
          {calculationRuleAddress ? (
            <div className={classNames.row6}>
              {updateUseCase ? (
                <ChangesPreview
                  loading={previewLoading}
                  releaseType={
                    upgradeNextVersion?.releaseType ?? ReleaseType.Initial
                  }
                  schema={upgradeNextVersion?.schema}
                  schemaDiff={upgradeNextVersion?.schemaDiff}
                  version={upgradeNextVersion?.version ?? "1.0.0"}
                />
              ) : (
                <ChangesPreview
                  loading={previewLoading}
                  releaseType={ReleaseType.Initial}
                  schema={newWebServiceSchema}
                  version="1.0.0"
                />
              )}
            </div>
          ) : null}
          <div className={classNames.row5}>
            {previewLoading ? (
              <LinearProgress />
            ) : (
              <DataDependencies scopes={dataDependencies} />
            )}
          </div>
          <div className={classNames.errorBoxesContainer}>
            {publishUseCaseError && (
              <ErrorBox
                apolloError={publishUseCaseError}
                title="Web service publish failed!"
                onClose={() => resetPublishUseCaseError()}
                closable
                maxHeight={updateUseCase ? 80 : 130}
              />
            )}
            {previewError && (
              <ErrorBox
                apolloError={previewError}
                title="Loading web service preview failed!"
                maxHeight={updateUseCase ? 80 : 130}
              />
            )}
          </div>
        </div>
      </Paper>
    </>
  );
}
